import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShoppingBag } from "lucide-react";
import { MENU, ORDER_URL } from "./data";

export default function MenuShowcase() {
  const [active, setActive] = useState(MENU[0].id);
  const category = MENU.find((c) => c.id === active);

  return (
    <section id="menu" data-testid="menu-section" className="scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E86A24]">Our Menu</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-[#3A2C1E] sm:text-3xl lg:text-4xl">
              A Taste of the Story
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-stone-500 sm:text-base">
              Our complete menu — from tandoori starters and designer curries to family meal deals. Order online for pickup or delivery.
            </p>
          </div>
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-view-full-menu-button"
            className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-[#C97B5F] px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#8A3324] transition-colors duration-200 hover:bg-[#D4A359] hover:text-[#0B0807]"
          >
            View Full Menu <ArrowUpRight size={14} />
          </a>
        </div>

        <div data-testid="menu-category-tabs" className="mt-10 flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {MENU.map((c) => (
            <button
              key={c.id}
              data-testid={`menu-category-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                active === c.id
                  ? "bg-gradient-to-r from-[#E86A24] to-[#B82E1F] text-white shadow-lg shadow-[#E86A24]/30"
                  : "border border-[#C97B5F]/25 text-stone-600 hover:border-[#C97B5F]/60 hover:text-[#8A3324]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="menu-items-grid"
          className="mt-8 grid gap-4 sm:grid-cols-2"
        >
          {category.note && (
            <p data-testid={`menu-category-note-${category.id}`} className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A3324]/80 sm:col-span-2">
              {category.note}
            </p>
          )}
          {category.items.map((item) => (
            <div
              key={item.name}
              data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex items-start justify-between gap-4 rounded-xl border border-[#C97B5F]/12 bg-white p-5 transition-colors duration-200 hover:border-[#E86A24]/40"
            >
              <div className="min-w-0">
                <h3 className="font-display text-base font-semibold text-[#3A2C1E]">{item.name}</h3>
                {item.desc && <p className="mt-1 text-sm text-stone-500">{item.desc}</p>}
              </div>
              <span className="shrink-0 whitespace-nowrap font-display text-base font-bold text-[#8A3324]">{item.price}</span>
            </div>
          ))}
        </motion.div>

        <a
          href={ORDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="menu-external-order-banner-button"
          className="card-glow group mt-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-[#E86A24]/30 bg-white p-7 text-center transition-colors duration-300 hover:border-[#E86A24]/60 sm:flex-row sm:text-left"
        >
          <div>
            <h3 className="font-display text-xl font-semibold text-[#3A2C1E] sm:text-2xl">Hungry? The full menu is one tap away.</h3>
            <p className="mt-2 text-sm text-stone-500">Order online for takeaway or delivery — fresh from our tandoor to your table.</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2.5 rounded-full bg-gradient-to-r from-[#E86A24] to-[#B82E1F] px-7 py-3.5 text-xs font-bold uppercase tracking-widest text-white shadow-xl shadow-[#E86A24]/30 transition-transform duration-200 group-hover:scale-[1.03]">
            <ShoppingBag size={15} /> Order Online Now
          </span>
        </a>
      </div>
    </section>
  );
}
